import React from "react";
import { Button } from "reactstrap";
import { useForm } from "react-hook-form";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import styles from "../pages/studydesign.module.scss";
import CommonForm, { FormFieldConfig } from "../../../../Components/Common/CommonForm";
import CommonTableIndexWise, { Column } from "../../../../Components/Common/CommonTableIndexWise";

/* ── Section Card ── */
const SectionCard: React.FC<{
  index: number;
  icon: string;
  title: string;
  subtitle: string;
  children: React.ReactNode;
  rightContent?: React.ReactNode;
}> = ({ index, icon, title, subtitle, children, rightContent }) => (
  <div className={formStyles.sectionCard}>
    <div className={formStyles.sectionHeader}>
      {/* <div className={formStyles.sectionBadge}>
        <span>{index}</span>
      </div> */}
      <i className={`${icon} ${formStyles.sectionIcon}`} />
      <div className={formStyles.sectionTitleWrap}>
        <p className={formStyles.sectionTitle}>{title}</p>
        <p className={formStyles.sectionSubtitle}>{subtitle}</p>
      </div>
      {rightContent && <div className="ms-auto">{rightContent}</div>}
    </div>
    <div className={formStyles.sectionBody}>
      {children}
    </div>
  </div>
);

const ReplicateHVDDesign: React.FC = () => {
  const { control, formState: { errors } } = useForm({
    defaultValues: {
      designType: "",
      periods: "",
      sequences: "",
      totalSubjects: "",
      washout: "",
    }
  });

  const studyDesignFields: FormFieldConfig[] = [
    { name: "designType", label: "Design Type", type: "select", options: [{ label: "Full Replicate (4-way)", value: "Full Replicate (4-way)" }] },
    { name: "periods", label: "Periods", type: "select", options: [{ label: "4", value: "4" }] },
    { name: "sequences", label: "Sequences", type: "select", options: [{ label: "2", value: "2" }] },
    { name: "totalSubjects", label: "Total Subjects", type: "number", placeholder:"Enter Total Subjects" },
    { name: "washout", label: "Washout (Days)", type: "number", placeholder:"Enter Washout Days" },
  ];

  const allocationColumns: Column[] = [
    { key: "sequence", label: "Sequence" },
    { key: "subjects", label: "Subjects (N)", textCenter: true, render: (val) => <div className={styles.subjectsCount}>{val}</div> },
    { key: "period1", label: "Period 1", render: (val) => <div className={styles.periodBadge}>{val}</div> },
    { key: "washout1", label: "Washout", render: (val) => <span className={styles.washoutText}>{val} Days</span> },
    { key: "period2", label: "Period 2", render: (val) => <div className={styles.periodBadge}>{val}</div> },
    { key: "washout2", label: "Washout", render: (val) => <span className={styles.washoutText}>{val} Days</span> },
    { key: "period3", label: "Period 3", render: (val) => <div className={styles.periodBadge}>{val}</div> },
    { key: "washout3", label: "Washout", render: (val) => <span className={styles.washoutText}>{val} Days</span> },
    { key: "period4", label: "Period 4", render: (val) => <div className={styles.periodBadge}>{val}</div> },
  ];

  const allocationData = [
    { id: 1, sequence: "TRTR", subjects: 18, period1: "Test (T)", washout1: 7, period2: "Reference (R)", washout2: 7, period3: "Test (T)", washout3: 7, period4: "Reference (R)" },
    { id: 2, sequence: "RTRT", subjects: 18, period1: "Reference (R)", washout1: 7, period2: "Test (T)", washout2: 7, period3: "Reference (R)", washout3: 7, period4: "Test (T)" },
  ];

  return (
    <React.Fragment>
      {/* 1. Study Design Parameters */}
      <SectionCard
        index={1}
        icon="ri-settings-3-line"
        title="Study Design Parameters"
        subtitle="Configure the core statistical parameters for the replicate design."
        rightContent={<span className={styles.badgeDraft}>Draft Mode</span>}
      >
        <div className={styles.fiveColumnForm}>
          <CommonForm fields={studyDesignFields} control={control} errors={errors} columns={6} />
        </div>
      </SectionCard>

      {/* 2. Allocation Table Section */}
      <SectionCard
        index={2}
        icon="ri-file-copy-2-line"
        title="Full Replicate 4-Period Allocation"
        subtitle="Review and manage the subject allocation across periods and sequences."
        rightContent={
          <Button color="primary" outline size="sm" className="d-flex align-items-center gap-2" style={{ backgroundColor: '#e0e7ff', border: 'none', color: '#4338ca' }}>
            <i className="ri-magic-line"></i> Auto-Generate Randomization
          </Button>
        }
      >
        <CommonTableIndexWise
          columns={allocationColumns}
          data={allocationData}
          paginationShow={false}
          hideAction={true}
        />

        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className={styles.legend}>
            <div className={styles.legendItem}>
              <div className={styles.dot} style={{ backgroundColor: '#1e3a8a' }}></div>
              Test Product (T)
            </div>
            <div className={styles.legendItem}>
              <div className={styles.dot} style={{ backgroundColor: '#e2e8f0' }}></div>
              Reference Product (R)
            </div>
          </div>
          <div className={styles.totalFooter}>
            Total Allocated Subjects: 36/36
          </div>
        </div>
      </SectionCard>

      {/* FDA Guidance */}
      <div className={styles.guidanceBox}>
        <i className="ri-information-line"></i>
        <div className={styles.guidanceContent}>
          <h6>FDA Guidance: Highly Variable Drugs (HVD)</h6>
          <p>
            A fully replicated design (TRTR / RTRT) is currently selected. This design allows for the calculation of intra-subject variability for both Test and Reference products, enabling reference-scaled average bioequivalence (RSABE) analysis if within-subject CV of the reference product is &gt; 30%.
          </p>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ReplicateHVDDesign;
